import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  createOnboardingPicks,
  getTaskStatus,
  startOnboardingImport,
} from "~/api/dropshipping/requests";
import styles from "./OnboardingFlow.module.css";
import {
  CURATED_PRODUCTS_PER_NICHE,
  IMPORT_AFTER_PAYMENT_KEY,
  MARKET_DETAILS,
  MAX_TASK_POLLS,
  ONBOARDING_MARKETS,
  ONBOARDING_NICHES,
  PREVIEW_PRODUCT_COUNT,
  TASK_POLL_INTERVAL_MS,
  type OnboardingMarket,
  type OnboardingNiche,
  type OnboardingProduct,
  type OnboardingTaskStatus,
  getAverageProfit,
  getDeliveryRange,
  getProductBadges,
  getProductImage,
  getProductPricing,
  getShippingDays,
  isTaskFinished,
  toggleNiche,
} from "./onboarding-utils";
import { OnboardingIcon } from "./OnboardingIcon";
import { useOnboardingTracking } from "./useOnboardingTracking";

// Must match ONBOARDING_PRODUCT_COUNT in jubilee-v2-api.
const PRODUCT_COUNT = 35;
const TOTAL_STEPS = 3;
// A failed status poll is retried; this many in a row means the import is lost.
const MAX_FAILED_POLLS = 3;
// Right after checkout the subscription can take a few seconds to land, and
// the import is refused until it does; retry the start this often.
const MAX_IMPORT_STARTS = 5;
const IMPORT_RETRY_DELAY_MS = 2000;
// Thumbnails in the "N of 35 found" row; the last one is shown faded.
const FOUND_THUMBNAILS = 6;

type Step =
  | "niches"
  | "market"
  | "loading"
  | "review"
  | "importing"
  | "success";

// What is kept across the checkout for the success screen's thumbnails.
type StoredPicks = Pick<
  OnboardingProduct,
  "id" | "title" | "image_cover_url"
>[];

type ImportResult = {
  imported: number;
  skipped: number;
  overLimit: number;
};

const readStoredPicks = (): StoredPicks => {
  try {
    const stored = JSON.parse(
      localStorage.getItem(IMPORT_AFTER_PAYMENT_KEY) ?? "[]"
    );
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
};

// The backend's own message (e.g. "User does not have an active subscription.").
const errorMessage = (error: unknown): string | null =>
  (error as { response?: { data?: { message?: string } } })?.response?.data
    ?.message ?? null;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type OnboardingFlowProps = {
  // The user has paid and chose "Import Products": import the picks the
  // backend kept and show the result.
  resumeImport: boolean;
  // "Import Products" or "Skip": open the existing checkout. The import runs
  // after payment, back in this flow with resumeImport set.
  onCheckout: (importProducts: boolean) => void;
  // The import is done (or given up on): show the app.
  onFinish: () => void;
};

export function OnboardingFlow({
  resumeImport,
  onCheckout,
  onFinish,
}: OnboardingFlowProps) {
  const { t } = useTranslation();
  const track = useOnboardingTracking();
  const [step, setStep] = useState<Step>(resumeImport ? "importing" : "niches");
  const [niches, setNiches] = useState<OnboardingNiche[]>([]);
  const [notSure, setNotSure] = useState(false);
  const [market, setMarket] = useState<OnboardingMarket>("US");
  const [products, setProducts] = useState<OnboardingProduct[]>([]);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [importedPicks, setImportedPicks] = useState<StoredPicks>([]);

  const [picksDone, setPicksDone] = useState(false);
  const [picksFailed, setPicksFailed] = useState(false);
  const picksRequest = useRef(0);

  useEffect(() => {
    track("onboarding_step_view", { step });
    // Once per step, not each time the tracker is recreated.
  }, [step]);

  useEffect(() => {
    if (resumeImport) setStep("importing");
  }, [resumeImport]);

  const nicheLabels = useMemo(
    () => niches.map((niche) => String(t(`onboarding_v2.niches.${niche}`))),
    [niches, t]
  );

  const findProducts = async () => {
    // Only the latest request may move the flow on.
    const request = ++picksRequest.current;
    setPicksDone(false);
    setPicksFailed(false);
    setStep("loading");
    try {
      const data = await createOnboardingPicks({
        niches: notSure ? [] : niches,
        market,
      });
      if (request !== picksRequest.current) return;
      setProducts(data.products as OnboardingProduct[]);
      setPicksDone(true);
      // Let the progress bar and "found" row finish before moving on.
      setTimeout(() => {
        if (request === picksRequest.current) setStep("review");
      }, 900);
    } catch {
      if (request === picksRequest.current) setPicksFailed(true);
    }
  };

  const skip = () => {
    track("onboarding_skip", { step });
    localStorage.removeItem(IMPORT_AFTER_PAYMENT_KEY);
    onCheckout(false);
  };

  const importProducts = () => {
    track("onboarding_import_click", { count: String(products.length) });
    const picks: StoredPicks = products.map(
      ({ id, title, image_cover_url }) => ({ id, title, image_cover_url })
    );
    localStorage.setItem(IMPORT_AFTER_PAYMENT_KEY, JSON.stringify(picks));
    onCheckout(true);
  };

  const finishImport = () => {
    localStorage.removeItem(IMPORT_AFTER_PAYMENT_KEY);
    onFinish();
  };

  return (
    <div className={styles.container}>
      {step === "niches" && (
        <NichesStep
          niches={niches}
          notSure={notSure}
          onToggleNiche={(niche) => {
            setNotSure(false);
            setNiches((selected) => toggleNiche(selected, niche));
          }}
          onToggleNotSure={() => {
            setNiches([]);
            setNotSure((value) => !value);
          }}
          onNext={() => setStep("market")}
          onSkip={skip}
        />
      )}
      {step === "market" && (
        <MarketStep
          market={market}
          onSelect={setMarket}
          onBack={() => setStep("niches")}
          onNext={findProducts}
          onSkip={skip}
        />
      )}
      {step === "loading" && (
        <LoadingStep
          done={picksDone}
          failed={picksFailed}
          products={products}
          nicheLabels={nicheLabels}
          market={market}
          onRetry={findProducts}
          onBack={() => setStep("market")}
        />
      )}
      {step === "review" && (
        <ReviewStep
          products={products}
          market={market}
          onBack={() => setStep("market")}
          onImport={importProducts}
          onSkip={skip}
        />
      )}
      {step === "importing" && (
        <ImportingStep
          onDone={(importResult) => {
            track("onboarding_import_done", {
              imported: String(importResult.imported),
            });
            setImportedPicks(readStoredPicks());
            localStorage.removeItem(IMPORT_AFTER_PAYMENT_KEY);
            setResult(importResult);
            setStep("success");
          }}
          onFail={() => track("onboarding_import_failed")}
          onGiveUp={finishImport}
        />
      )}
      {step === "success" && result && (
        <SuccessStep
          picks={importedPicks}
          result={result}
          onDashboard={onFinish}
        />
      )}
    </div>
  );
}

function StepHeader({ current, title }: { current: number; title: string }) {
  const { t } = useTranslation();
  return (
    <div className={styles.header}>
      <div className={styles.stepIndicator}>
        <span className={styles.stepLabel}>
          {String(t("onboarding_v2.step_of", { current, total: TOTAL_STEPS }))}
        </span>
        <div className={styles.stepBars} aria-hidden="true">
          {Array.from({ length: TOTAL_STEPS }, (_, index) => (
            <span
              key={index}
              className={`${styles.stepBar} ${index < current ? styles.stepBarActive : ""}`}
            />
          ))}
        </div>
      </div>
      <h2 className={styles.title}>{title}</h2>
    </div>
  );
}

function PrimaryButton({
  children,
  onClick,
  disabled,
  arrow,
}: {
  children: string;
  onClick: () => void;
  disabled?: boolean;
  arrow?: boolean;
}) {
  return (
    <button
      type="button"
      className={styles.primaryButton}
      onClick={onClick}
      disabled={disabled}>
      {children}
      {arrow && <OnboardingIcon name="arrow-right" />}
    </button>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button type="button" className={styles.textButton} onClick={onClick}>
      <OnboardingIcon name="arrow-left" />
      {String(t("onboarding_v2.back"))}
    </button>
  );
}

function SkipButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button type="button" className={styles.textButton} onClick={onClick}>
      {label}
    </button>
  );
}

function NichesStep({
  niches,
  notSure,
  onToggleNiche,
  onToggleNotSure,
  onNext,
  onSkip,
}: {
  niches: OnboardingNiche[];
  notSure: boolean;
  onToggleNiche: (niche: OnboardingNiche) => void;
  onToggleNotSure: () => void;
  onNext: () => void;
  onSkip: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className={styles.card}>
      <StepHeader current={1} title={String(t("onboarding_v2.niches.title"))} />

      <div className={styles.tileGrid}>
        {ONBOARDING_NICHES.map((niche) => {
          const selected = niches.includes(niche);
          return (
            <button
              key={niche}
              type="button"
              aria-pressed={selected}
              className={`${styles.tile} ${selected ? styles.tileSelected : ""}`}
              onClick={() => onToggleNiche(niche)}>
              <span className={styles.tileIcon}>
                <OnboardingIcon name={niche} />
              </span>
              <span className={styles.tileText}>
                <span className={styles.tileName}>
                  {String(t(`onboarding_v2.niches.${niche}`))}
                </span>
                <span className={styles.tileSub}>
                  {String(
                    t("onboarding_v2.niches.curated_count", {
                      count: CURATED_PRODUCTS_PER_NICHE,
                    })
                  )}
                </span>
              </span>
              <span className={styles.checkbox} aria-hidden="true">
                {selected && <OnboardingIcon name="check" />}
              </span>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        aria-pressed={notSure}
        className={`${styles.tile} ${notSure ? styles.tileSelected : ""}`}
        onClick={onToggleNotSure}>
        <span className={styles.tileIcon}>
          <OnboardingIcon name="sparkles" />
        </span>
        <span className={styles.tileText}>
          <span className={styles.tileName}>
            {String(
              t("onboarding_v2.niches.not_sure_title", { count: PRODUCT_COUNT })
            )}
          </span>
          <span className={styles.tileSub}>
            {String(
              t("onboarding_v2.niches.not_sure_description", {
                count: PRODUCT_COUNT,
              })
            )}
          </span>
        </span>
        <span className={styles.checkbox} aria-hidden="true">
          {notSure && <OnboardingIcon name="check" />}
        </span>
      </button>

      <div className={styles.divider} />

      <div className={styles.footer}>
        <SkipButton
          label={String(t("onboarding_v2.skip_setup"))}
          onClick={onSkip}
        />
        <div className={styles.footerEnd}>
          {niches.length > 0 && (
            <span className={styles.selectedCount}>
              {String(
                t("onboarding_v2.selected_count", { count: niches.length })
              )}
            </span>
          )}
          <PrimaryButton
            arrow
            disabled={niches.length === 0 && !notSure}
            onClick={onNext}>
            {String(t("onboarding_v2.next"))}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function MarketStep({
  market,
  onSelect,
  onBack,
  onNext,
  onSkip,
}: {
  market: OnboardingMarket;
  onSelect: (market: OnboardingMarket) => void;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div className={styles.card}>
      <StepHeader
        current={2}
        title={String(t("onboarding_v2.markets.title"))}
      />

      <div>
        <div className={styles.sectionLabel} id="onboarding-market-label">
          {String(t("onboarding_v2.markets.label"))}
        </div>
        <div
          className={styles.tileGrid}
          role="radiogroup"
          aria-labelledby="onboarding-market-label">
          {ONBOARDING_MARKETS.map((code) => {
            const selected = market === code;
            const details = MARKET_DETAILS[code];
            return (
              <button
                key={code}
                type="button"
                role="radio"
                aria-checked={selected}
                className={`${styles.tile} ${selected ? styles.tileSelected : ""}`}
                onClick={() => onSelect(code)}>
                <span className={styles.tileIcon}>
                  {code === "WORLDWIDE" ? (
                    <OnboardingIcon name="globe" />
                  ) : (
                    <span className={styles.marketCode}>{code}</span>
                  )}
                </span>
                <span className={styles.tileText}>
                  <span className={styles.tileName}>
                    {String(t(`onboarding_v2.markets.${code}`))}
                    {code === "US" && (
                      <span className={styles.badge}>
                        {String(t("onboarding_v2.markets.most_popular"))}
                      </span>
                    )}
                  </span>
                  <span className={styles.tileSub}>
                    <OnboardingIcon name="truck" />
                    {String(
                      t("onboarding_v2.markets.details", {
                        days: details.deliveryDays,
                        products: details.productCount,
                      })
                    )}
                  </span>
                </span>
                <span className={styles.radio} aria-hidden="true" />
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.footer}>
        <BackButton onClick={onBack} />
        <div className={styles.footerEnd}>
          <SkipButton
            label={String(t("onboarding_v2.skip_setup"))}
            onClick={onSkip}
          />
          <PrimaryButton arrow onClick={onNext}>
            {String(t("onboarding_v2.markets.find_products"))}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function LoadingStep({
  done,
  failed,
  products,
  nicheLabels,
  market,
  onRetry,
  onBack,
}: {
  done: boolean;
  failed: boolean;
  products: OnboardingProduct[];
  nicheLabels: string[];
  market: OnboardingMarket;
  onRetry: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  // The request has no real progress to report, so the bar eases towards 90%
  // while it runs and jumps to 100% when it returns.
  const [progress, setProgress] = useState(5);

  useEffect(() => {
    if (done || failed) return;
    const timer = setInterval(
      () => setProgress((value) => Math.min(90, value + (90 - value) * 0.12)),
      300
    );
    return () => clearInterval(timer);
  }, [done, failed]);

  if (failed) {
    return (
      <div className={styles.card}>
        <div className={styles.errorText}>
          {String(t("onboarding_v2.loading.error"))}
        </div>
        <div className={styles.footer}>
          <BackButton onClick={onBack} />
          <div className={styles.footerEnd}>
            <PrimaryButton onClick={onRetry}>
              {String(t("onboarding_v2.loading.retry"))}
            </PrimaryButton>
          </div>
        </div>
      </div>
    );
  }

  const shownProgress = done ? 100 : Math.round(progress);
  const secondsLeft = Math.max(1, Math.ceil((100 - shownProgress) / 8));
  const found = done
    ? products.length
    : Math.floor((shownProgress / 100) * PRODUCT_COUNT);
  const { deliveryDays } = MARKET_DETAILS[market];

  const checklist = [
    String(t("onboarding_v2.loading.step_scanned")),
    nicheLabels.length > 0
      ? String(
          t("onboarding_v2.loading.step_matched", {
            niches: nicheLabels.join(", "),
          })
        )
      : String(t("onboarding_v2.loading.step_matched_trending")),
    market === "WORLDWIDE"
      ? String(
          t("onboarding_v2.loading.step_delivery_worldwide", {
            days: deliveryDays,
          })
        )
      : String(
          t("onboarding_v2.loading.step_delivery", {
            days: deliveryDays,
            place: String(t(`onboarding_v2.loading.places.${market}`)),
          })
        ),
    String(t("onboarding_v2.loading.step_pricing")),
  ];
  const activeIndex = done
    ? checklist.length
    : Math.min(checklist.length - 1, Math.floor(shownProgress / 25));

  const thumbnails = Array.from(
    { length: FOUND_THUMBNAILS },
    (_, index) => products[index]
  );

  return (
    <div className={`${styles.card} ${styles.loading}`}>
      <div className={styles.spinner}>
        <OnboardingIcon name="sparkles" size={22} />
      </div>
      <h2 className={styles.loadingTitle}>
        {String(t("onboarding_v2.loading.title", { count: PRODUCT_COUNT }))}
      </h2>

      <div className={styles.progress}>
        <div
          className={styles.progressTrack}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={shownProgress}>
          <div
            className={styles.progressFill}
            style={{ width: `${shownProgress}%` }}
          />
        </div>
        <div className={styles.progressLabels}>
          <span className={styles.progressPercent}>{`${shownProgress}%`}</span>
          {!done && (
            <span>
              {String(
                t("onboarding_v2.loading.seconds_left", { count: secondsLeft })
              )}
            </span>
          )}
        </div>
      </div>

      <div className={styles.checklist}>
        {checklist.map((label, index) => {
          const isDone = index < activeIndex;
          const isActive = index === activeIndex;
          return (
            <div
              key={label}
              className={`${styles.checkItem} ${isActive ? styles.checkItemActive : ""} ${isDone || isActive ? "" : styles.checkItemPending}`}>
              <span
                className={`${styles.checkDot} ${isDone ? styles.checkDotDone : ""} ${isActive ? styles.checkDotActive : ""}`}
                aria-hidden="true">
                {isDone && <OnboardingIcon name="check" size={11} />}
              </span>
              {label}
            </div>
          );
        })}
      </div>

      <div className={styles.foundPreview}>
        <div className={styles.thumbRow} aria-hidden="true">
          {thumbnails.map((product, index) =>
            product && done ? (
              <img
                key={product.id}
                className={`${styles.thumb} ${index === FOUND_THUMBNAILS - 1 ? styles.thumbFaded : ""}`}
                src={getProductImage(product)}
                alt=""
              />
            ) : (
              <span
                key={index}
                className={`${styles.thumb} ${index === FOUND_THUMBNAILS - 1 ? styles.thumbFaded : ""}`}
              />
            )
          )}
        </div>
        <span>
          {String(
            t("onboarding_v2.loading.found", {
              found,
              total: PRODUCT_COUNT,
            })
          )}
        </span>
      </div>
    </div>
  );
}

function ProductCard({
  product,
  badge,
}: {
  product: OnboardingProduct;
  badge?: string;
}) {
  const { t } = useTranslation();
  const pricing = getProductPricing(product);
  const image = getProductImage(product);
  const shippingDays = getShippingDays(product);

  return (
    <div className={styles.productCard}>
      <div className={styles.productImage}>
        {image && <img src={image} alt={product.title} loading="lazy" />}
        {badge && <span className={styles.productBadge}>{badge}</span>}
        <span className={styles.productCheck} aria-hidden="true">
          <OnboardingIcon name="check" />
        </span>
      </div>
      <div className={styles.productBody}>
        <div className={styles.productTitle} title={product.title}>
          {product.title}
        </div>
        {shippingDays && (
          <div className={styles.productShips}>
            {String(t("onboarding_v2.review.ships_in", { days: shippingDays }))}
          </div>
        )}
        {pricing && (
          <div className={styles.productPrices}>
            <div className={styles.priceBlock}>
              <span className={styles.priceLabel}>
                {String(t("onboarding_v2.review.sell"))}
              </span>
              <span className={styles.priceValue}>{pricing.sell}</span>
            </div>
            <div className={`${styles.priceBlock} ${styles.priceBlockEnd}`}>
              <span className={styles.priceLabel}>
                {String(t("onboarding_v2.review.profit"))}
              </span>
              <span className={`${styles.priceValue} ${styles.profitValue}`}>
                {pricing.profit}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReviewStep({
  products,
  market,
  onBack,
  onImport,
  onSkip,
}: {
  products: OnboardingProduct[];
  market: OnboardingMarket;
  onBack: () => void;
  onImport: () => void;
  onSkip: () => void;
}) {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const averageProfit = useMemo(() => getAverageProfit(products), [products]);
  const badges = useMemo(() => getProductBadges(products), [products]);

  if (products.length === 0) {
    return (
      <div className={`${styles.card} ${styles.success}`}>
        <div className={styles.successText}>
          <h2 className={styles.title}>
            {String(t("onboarding_v2.review.empty_title"))}
          </h2>
          <p className={styles.successSubtitle}>
            {String(t("onboarding_v2.review.empty_description"))}
          </p>
        </div>
        <div className={styles.centered}>
          <PrimaryButton onClick={onBack}>
            {String(t("onboarding_v2.back"))}
          </PrimaryButton>
        </div>
      </div>
    );
  }

  const hiddenCount = products.length - PREVIEW_PRODUCT_COUNT;
  const visibleProducts =
    showAll || hiddenCount <= 0
      ? products
      : products.slice(0, PREVIEW_PRODUCT_COUNT);
  const hiddenPreview = products.slice(
    PREVIEW_PRODUCT_COUNT,
    PREVIEW_PRODUCT_COUNT + 3
  );
  const { deliveryDays } = MARKET_DETAILS[market];

  const stats = [
    {
      icon: <OnboardingIcon name="package" />,
      value: String(products.length),
      label: String(t("onboarding_v2.review.products_selected")),
    },
    ...(averageProfit
      ? [
          {
            icon: <OnboardingIcon name="trend" />,
            value: averageProfit,
            label: String(t("onboarding_v2.review.avg_profit")),
          },
        ]
      : []),
    {
      icon: <OnboardingIcon name="truck" size={16} />,
      value: String(
        t("onboarding_v2.review.delivery_value", {
          // The picked products' real range; the market's typical one if
          // none of them says.
          days: getDeliveryRange(products) ?? deliveryDays,
        })
      ),
      label:
        market === "WORLDWIDE"
          ? String(t("onboarding_v2.review.delivery_label_worldwide"))
          : String(t("onboarding_v2.review.delivery_label", { code: market })),
    },
    {
      icon: <OnboardingIcon name="sparkles" />,
      value: String(t("onboarding_v2.review.ai_picked")),
      label: String(t("onboarding_v2.review.ai_picked_label")),
    },
  ];

  return (
    <div className={styles.card}>
      <StepHeader
        current={3}
        title={String(
          t("onboarding_v2.review.title", { count: products.length })
        )}
      />

      <div className={styles.stats}>
        {stats.map((stat) => (
          <div key={stat.label} className={styles.stat}>
            <span className={styles.statIcon}>{stat.icon}</span>
            <span className={styles.tileText}>
              <span className={styles.statValue}>{stat.value}</span>
              <span className={styles.statLabel}>{stat.label}</span>
            </span>
          </div>
        ))}
      </div>

      <div>
        <div className={styles.productGrid}>
          {visibleProducts.map((product) => {
            const badge = badges.get(product.id);
            return (
              <ProductCard
                key={product.id}
                product={product}
                badge={
                  badge
                    ? String(t(`onboarding_v2.review.badges.${badge}`))
                    : undefined
                }
              />
            );
          })}
          {!showAll && hiddenCount > 0 && (
            <div className={styles.moreTile}>
              <div className={styles.avatarStack} aria-hidden="true">
                {hiddenPreview.map((product) => (
                  <img
                    key={product.id}
                    src={getProductImage(product)}
                    alt=""
                    loading="lazy"
                  />
                ))}
              </div>
              <span className={styles.moreCount}>
                {String(t("onboarding_v2.review.more", { count: hiddenCount }))}
              </span>
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => setShowAll(true)}>
                {String(t("onboarding_v2.review.review_all"))}
                <OnboardingIcon name="arrow-right" size={12} />
              </button>
            </div>
          )}
        </div>
        {showAll && hiddenCount > 0 && (
          <div className={styles.centered}>
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => setShowAll(false)}>
              {String(t("onboarding_v2.review.show_less"))}
            </button>
          </div>
        )}
      </div>

      <div className={styles.footer}>
        <BackButton onClick={onBack} />
        <div className={styles.footerEnd}>
          <SkipButton
            label={String(t("onboarding_v2.review.skip"))}
            onClick={onSkip}
          />
          <PrimaryButton onClick={onImport}>
            {String(t("onboarding_v2.review.import_products"))}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

// After payment: import the picks the backend kept, polling the task.
function ImportingStep({
  onDone,
  onFail,
  onGiveUp,
}: {
  onDone: (result: ImportResult) => void;
  onFail: () => void;
  onGiveUp: () => void;
}) {
  const { t } = useTranslation();
  const [task, setTask] = useState<OnboardingTaskStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const fail = (message?: string | null) => {
      if (cancelled) return;
      onFail();
      setError(message || String(t("onboarding_v2.importing.error")));
    };

    const run = async () => {
      setError(null);
      setTask(null);

      let taskId: string | null = null;
      let lastError: unknown = null;
      for (let start = 0; start < MAX_IMPORT_STARTS && !taskId; start += 1) {
        if (start > 0) await wait(IMPORT_RETRY_DELAY_MS);
        if (cancelled) return;
        try {
          const data = await startOnboardingImport();
          taskId = String(data.task_id);
        } catch (startError) {
          lastError = startError;
        }
      }
      if (!taskId) return fail(errorMessage(lastError));

      let failedPolls = 0;
      for (let poll = 0; poll < MAX_TASK_POLLS; poll += 1) {
        if (poll > 0) await wait(TASK_POLL_INTERVAL_MS);
        if (cancelled) return;
        let status: OnboardingTaskStatus;
        try {
          status = await getTaskStatus(taskId);
        } catch {
          failedPolls += 1;
          if (failedPolls >= MAX_FAILED_POLLS) return fail();
          continue;
        }
        failedPolls = 0;
        if (cancelled) return;
        setTask(status);
        if (isTaskFinished(status.status)) {
          const imported = status.result_data?.imported_count ?? 0;
          if (status.status === "completed" && imported) {
            onDone({
              imported,
              skipped: status.result_data?.failed_product_ids.length ?? 0,
              overLimit: status.result_data?.over_limit_count ?? 0,
            });
            return;
          }
          return fail();
        }
      }
      fail();
    };

    void run();
    return () => {
      cancelled = true;
    };
    // Runs again only on "Try again".
  }, [attempt]);

  if (error) {
    return (
      <div className={styles.card}>
        <div className={styles.errorText}>{error}</div>
        <div className={styles.footer}>
          <SkipButton
            label={String(t("onboarding_v2.importing.go_to_dashboard"))}
            onClick={onGiveUp}
          />
          <div className={styles.footerEnd}>
            <PrimaryButton onClick={() => setAttempt((value) => value + 1)}>
              {String(t("onboarding_v2.importing.retry"))}
            </PrimaryButton>
          </div>
        </div>
      </div>
    );
  }

  const total = task?.total_items || PRODUCT_COUNT;
  const processed = task?.processed_items ?? 0;
  const percent = Math.round((processed / total) * 100);

  return (
    <div className={`${styles.card} ${styles.loading}`}>
      <div className={styles.spinner}>
        <OnboardingIcon name="sparkles" size={22} />
      </div>
      <h2 className={styles.loadingTitle}>
        {String(t("onboarding_v2.importing.title"))}
      </h2>
      <div className={styles.progress}>
        <div
          className={styles.progressTrack}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}>
          <div
            className={styles.progressFill}
            style={{ width: `${percent}%` }}
          />
        </div>
        <div className={styles.progressLabels}>
          <span className={styles.progressPercent}>{`${percent}%`}</span>
          <span>
            {String(
              t("onboarding_v2.importing.progress", { processed, total })
            )}
          </span>
        </div>
      </div>
    </div>
  );
}

function SuccessStep({
  picks,
  result,
  onDashboard,
}: {
  picks: StoredPicks;
  result: ImportResult;
  onDashboard: () => void;
}) {
  const { t } = useTranslation();
  const thumbnails = picks.slice(0, PREVIEW_PRODUCT_COUNT);
  const remaining = result.imported - thumbnails.length;

  return (
    <div className={`${styles.card} ${styles.success}`}>
      <div className={styles.successIcon}>
        <OnboardingIcon name="check" size={22} />
      </div>
      <div className={styles.successText}>
        <h2 className={styles.title}>
          {String(t("onboarding_v2.success.title", { count: result.imported }))}
        </h2>
        <p className={styles.successSubtitle}>
          {String(t("onboarding_v2.success.description"))}
        </p>
        {result.overLimit > 0 && (
          <p className={styles.successSubtitle}>
            {String(
              t("onboarding_v2.success.over_limit", {
                count: result.overLimit,
              })
            )}
          </p>
        )}
        {result.skipped > 0 && (
          <p className={styles.successSubtitle}>
            {String(
              t("onboarding_v2.success.partial", { count: result.skipped })
            )}
          </p>
        )}
      </div>

      <div className={styles.thumbRow}>
        {thumbnails.map((product) => (
          <img
            key={product.id}
            className={styles.thumb}
            src={product.image_cover_url ?? ""}
            alt={product.title}
            loading="lazy"
          />
        ))}
        {remaining > 0 && (
          <span className={`${styles.thumb} ${styles.thumbMore}`}>
            {`+${remaining}`}
          </span>
        )}
      </div>

      <div className={styles.centered}>
        <PrimaryButton arrow onClick={onDashboard}>
          {String(t("onboarding_v2.success.go_to_dashboard"))}
        </PrimaryButton>
      </div>
    </div>
  );
}
